package org.example.project.screens_sys

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.example.project.manager.LumaticaApiManager
import org.example.project.enginetabular.loadWithTabularFilters
import org.example.project.manager.LumaticaApiSettings
import org.example.project.service.GlobalManager
import org.example.project.utils.All
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

object KundenSetupApi {
    private const val API = "kunensetup"

    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    suspend fun loadCompanies(): List<All> =
        decodeList(LumaticaApiManager.call("$API/companies"))
            .withoutInternalSystemCompanies()

    suspend fun loadAllDetails(): List<All> =
        decodeList(LumaticaApiManager.call("$API/details-all"))

    suspend fun searchSetup(filters: Map<String, String>): List<All> =
        decodeList(loadWithTabularFilters("$API/setup-tabular", filters))
            .withoutInternalSystemCompanies()

    suspend fun saveDetails(firmenId: String, data: All): All? =
        decodeOne(
            LumaticaApiManager.call(
                "$API/details-save-json/${apiPart(firmenId)}/${apiPart(json.encodeToString(All.AllNames.serializer(), data))}"
            )
        )?.also {
            markCompanyChanged(firmenId)
        }

    suspend fun loadModules(firmenId: String): List<All> =
        decodeList(LumaticaApiManager.call("$API/customer-modules/${apiPart(firmenId)}/false"))

    suspend fun searchModuleAuskunft(filters: Map<String, String>): List<All> =
        decodeList(loadWithTabularFilters("$API/module-tabular", filters))

    suspend fun customerNodeExists(firmenId: String): Boolean =
        decodeBool(LumaticaApiManager.call("$API/customer-node-exists/${apiPart(firmenId)}"), "exists")

    suspend fun ensureCustomerNodeSetup(firmenId: String, lizenzkey: String, nodePort: String): Boolean =
        decodeBool(
            LumaticaApiManager.call(
                "$API/customer-node-setup/${apiPart(firmenId)}/${apiPart(lizenzkey)}/${apiPart(nodePort)}"
            ),
            "ok"
        )

    suspend fun saveCompany(firma: All): All? =
        decodeOne(postCompanyJson("$API/save-company-json", firma))
            ?.also(::markCompanyChanged)

    private fun decodeOne(raw: String): All? =
        runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()

    private fun decodeList(raw: String): List<All> =
        runCatching { json.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }.getOrDefault(emptyList())

    private fun decodeBool(raw: String, key: String): Boolean =
        raw.contains("\"$key\":true")

    private fun markCompanyChanged(firma: All) {
        markCompanyChanged(firma.firmenId.ifBlank { firma.id })
    }

    private fun markCompanyChanged(firmenId: String) {
        if (firmenId.isNotBlank()) {
            GlobalManager.markJsonChanged("firma.json", "register/$firmenId/firma.json")
        }
    }

    private suspend fun postCompanyJson(endpoint: String, firma: All): String {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$endpoint",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(All.AllNames.serializer(), firma)
            )
        ).await()
        return response.text().await()
    }

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String
}
